import { useEffect, useRef, useState } from 'react';

// Generic sticky-left section navigation with scroll-spy. Originally
// landed inside the rollouts module as BuildViewSideMenu; extracted
// here in Build View polish round 2 so the same pattern can also
// drive RowEditor's existing sticky <nav> (which had click-jump but
// no scroll-spy) and CampaignForm's new side menu.
//
// What it does
//   - Renders a <nav> column of clickable section labels
//   - Click → smooth-scrolls the corresponding section into view via
//     scrollIntoView (anchor href stays for keyboard accessibility,
//     preventDefault avoids the jump-then-smooth-scroll flash)
//   - Highlights the section currently in view via per-section
//     IntersectionObservers
//   - Active-section detection: a section becomes active when its top
//     edge crosses ~40% from the top of the viewport (configurable
//     via `rootMargin`). The "topmost intersecting" rule keeps the
//     highlight stable when two sections are partially visible
//
// Visibility
//   `visible: false` excludes a section from the menu — handy when a
//   surface conditionally renders sections (Lead Form on lead_gen
//   rows; Meta Content when the Account runs Meta). Callers pass the
//   full list and let the component filter; that keeps the section-
//   list construction co-located with the page's render logic.
//
// Layout convention
//   Callers wrap their page in `grid grid-cols-[200px_1fr] gap-8
//   max-w-5xl mx-auto` (or close) so the 200-pixel sticky column sits
//   alongside a content column. The nav itself is `sticky top-4
//   self-start`.

export type StickyNavSection = {
  id: string;
  label: string;
  // When `false`, the section is filtered out of the menu. Defaults
  // to `true`. Useful for sections that conditionally render (e.g.
  // Lead Form only when ad type is lead_gen).
  visible?: boolean;
};

type Props = {
  sections: StickyNavSection[];
  // IntersectionObserver rootMargin override. Default biases the
  // "active" zone to roughly the upper-middle of the viewport, which
  // matches the "what is the user looking at right now" intuition
  // and avoids flickering at scroll boundaries.
  rootMargin?: string;
  // Smooth-scroll on click. Defaults to true. Pass false to opt into
  // an instant jump (e.g. if the page itself is short).
  smoothScroll?: boolean;
  // Aria-label for the <nav>. Defaults to "Section navigation"; pass
  // a more specific label per surface ("Build View sections", "Row
  // editor sections", etc.) so screen-reader users hear which menu
  // they're in.
  ariaLabel?: string;
  // Polish round 3 (Fix 2): optional slot for surface-level actions
  // — typically Save / Cancel buttons on editing surfaces. Renders
  // below the section list, separated by a top border. Read-mostly
  // surfaces (BuildView, CampaignDetail) leave this undefined and
  // get just the section anchors. Buttons stack vertically in the
  // 200-pixel column; callers usually pass full-width <Button>s.
  actions?: React.ReactNode;
};

const DEFAULT_ROOT_MARGIN = '-40% 0px -55% 0px';

export function StickyNav({
  sections,
  rootMargin = DEFAULT_ROOT_MARGIN,
  smoothScroll = true,
  ariaLabel = 'Section navigation',
  actions,
}: Props) {
  const visibleSections = sections.filter((s) => s.visible !== false);

  const [activeId, setActiveId] = useState<string | null>(
    visibleSections[0]?.id ?? null,
  );
  // Latest visible-sections array kept in a ref so the
  // IntersectionObserver callback can read the current list without
  // re-creating the observer on every prop change. The observer is
  // rebuilt only when the section ids actually change (see effect
  // below).
  const sectionsRef = useRef(visibleSections);
  sectionsRef.current = visibleSections;

  // The set of currently-intersecting section ids. Survives across
  // observer callbacks via a ref so the "topmost intersecting" rule
  // produces a stable answer when entries arrive in batches.
  const intersectingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (visibleSections.length === 0) return;
    intersectingRef.current = new Set();
    const observers: IntersectionObserver[] = [];
    for (const section of visibleSections) {
      const el = document.getElementById(section.id);
      if (!el) continue;
      const obs = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              intersectingRef.current.add(section.id);
            } else {
              intersectingRef.current.delete(section.id);
            }
          }
          // Pick the topmost-in-DOM section that's currently
          // intersecting. Iteration order over `sectionsRef.current`
          // preserves the page order so the menu reads top-down.
          for (const s of sectionsRef.current) {
            if (intersectingRef.current.has(s.id)) {
              setActiveId(s.id);
              return;
            }
          }
        },
        { rootMargin, threshold: 0 },
      );
      obs.observe(el);
      observers.push(obs);
    }
    return () => {
      for (const o of observers) o.disconnect();
    };
    // Only re-run when the section ids or rootMargin change. Joining
    // the ids stays cheap and avoids re-binding observers on every
    // parent re-render.
  }, [visibleSections.map((s) => s.id).join('|'), rootMargin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({
      behavior: smoothScroll ? 'smooth' : 'auto',
      block: 'start',
    });
    // Update active immediately for snappy feedback — the
    // IntersectionObserver will re-confirm in a moment, but waiting
    // for the smooth-scroll to settle would lag the highlight by
    // ~half a second.
    setActiveId(id);
  };

  return (
    <nav className="sticky top-4 self-start" aria-label={ariaLabel}>
      <div className="space-y-1">
        {visibleSections.map((s) => {
          const active = s.id === activeId;
          return (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => handleClick(e, s.id)}
              className={
                'block px-3 py-1.5 rounded text-sm transition-colors ' +
                (active
                  ? 'bg-slate-900 text-white font-medium'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900')
              }
              aria-current={active ? 'true' : undefined}
            >
              {s.label}
            </a>
          );
        })}
      </div>
      {actions !== undefined && actions !== null && (
        // Visual separator: top border + padding break between the
        // section anchors above and the actions below. Matches the
        // muted-rail aesthetic of the section links rather than
        // calling attention to the actions block — the buttons
        // themselves carry the emphasis (primary blue Save, etc.).
        <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
          {actions}
        </div>
      )}
    </nav>
  );
}
